import { Link } from 'react-router-dom'
import { Activity, AlertTriangle, ChevronRight } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAgents } from '@/hooks/useAgents'
import type { AgentSummary } from '@/lib/api'

// Map an agent's domain to a plain-English noun for its daily runs, so the
// breakdown reads "Clio: 3 content runs" rather than "Clio: 3 activations".
function actionNoun(domain: string | null | undefined, n: number): string {
  const d = (domain ?? '').toLowerCase()
  const plural = (s: string) => (n === 1 ? s.replace(/s$/, '') : s)
  if (d.includes('content') || d.includes('seo')) return plural('content runs')
  if (d.includes('communication') || d.includes('pr')) return plural('comms drafts')
  if (d.includes('driver')) return plural('driver messages')
  if (d.includes('sales')) return plural('pipeline scans')
  if (d.includes('customer')) return plural('health checks')
  if (d.includes('finance')) return plural('finance runs')
  if (d.includes('security')) return plural('security checks')
  if (d.includes('market')) return plural('market scans')
  if (d.includes('product')) return n === 1 ? 'product analysis' : 'product analyses'
  if (d.includes('compliance')) return plural('compliance checks')
  if (d.includes('partnership')) return plural('integration checks')
  if (d.includes('dev ops') || d.includes('devops')) return plural('health checks')
  return plural('runs')
}

function failed(outcome: string | null): boolean {
  const o = (outcome ?? '').toLowerCase()
  return o === 'failed' || o === 'failure' || o === 'error' || o === 'dead-letter'
}

type AttentionKind = 'failed' | 'pending' | 'lowrate'

interface Attention {
  agent: AgentSummary
  reason: string
  kind: AttentionKind
}

function needsAttention(a: AgentSummary): Attention | null {
  if (failed(a.last_run_outcome)) return { agent: a, reason: 'last run failed', kind: 'failed' }
  if (a.pending_decisions > 0) {
    return {
      agent: a,
      reason: `${a.pending_decisions} decision${a.pending_decisions === 1 ? '' : 's'} waiting on you`,
      kind: 'pending',
    }
  }
  if (a.runs_today > 0 && a.success_rate_24h !== null && a.success_rate_24h < 50) {
    return { agent: a, reason: `success rate down to ${a.success_rate_24h}%`, kind: 'lowrate' }
  }
  return null
}

/** Where each attention item should take Mark. A failed (or low-rate) run links
 *  straight to the activity log pre-filtered to that agent's failures, so the
 *  failing run is one click away instead of a hunt. */
function linkFor({ agent, kind }: Attention): string {
  const slug = encodeURIComponent(agent.name)
  switch (kind) {
    case 'failed':  return `/dashboard/activity?agent=${slug}&severity=FAILED`
    case 'pending': return `/dashboard/decisions?agent=${slug}`
    case 'lowrate': return `/dashboard/activity?agent=${slug}&severity=FAILED`
  }
}

/**
 * "Today's Agent Activity" — a plain-English digest of what the workforce did
 * today: total actions, a per-agent breakdown, and anything that needs Mark.
 * Lives on the dashboard home; reads the same /agents summary the Agents page does.
 */
export function AgentActivitySummary() {
  const { data, isLoading } = useAgents()
  const agents = data?.data ?? []

  const active = agents
    .filter((a) => a.runs_today > 0)
    .sort((a, b) => b.runs_today - a.runs_today)
  const totalActions = agents.reduce((sum, a) => sum + a.runs_today, 0)
  const attention = agents.map(needsAttention).filter((x): x is Attention => x !== null)

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>TODAY'S AGENT ACTIVITY</CardTitle>
        <span className="flex items-center gap-1.5 font-mono text-[0.65rem] text-cmd-outline tabular-nums">
          <Activity className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          {totalActions} ACTIONS
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/* Needs attention */}
            {attention.length > 0 && (
              <div className="space-y-1 bg-cmd-error-container/30 p-2.5">
                {attention.map((item) => (
                  <Link
                    key={item.agent.name}
                    to={linkFor(item)}
                    className="flex items-center gap-2 text-[0.8rem] text-white/90 hover:text-white group"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[#ffd080]" aria-hidden="true" />
                    <span className="flex-1">
                      <span className="font-medium">{item.agent.display_name}</span>
                      <span className="text-cmd-outline"> — {item.reason}</span>
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-cmd-outline opacity-0 group-hover:opacity-100" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            )}

            {/* Per-agent breakdown */}
            {active.length === 0 ? (
              <p className="font-label text-sm text-cmd-outline">
                NO AGENT RUNS YET TODAY — the workforce is idle or waiting on schedule.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {active.map((a) => (
                  <li key={a.name} className="flex items-baseline justify-between gap-3 text-[0.8rem]">
                    <span className="text-white/90">
                      <span className="font-medium">{a.display_name}</span>
                      <span className="text-cmd-outline">
                        {': '}
                        {a.runs_today} {actionNoun(a.domain, a.runs_today)}
                      </span>
                    </span>
                    {a.success_rate_24h !== null && (
                      <span className="shrink-0 font-mono text-[0.65rem] text-cmd-outline-variant tabular-nums">
                        {a.success_rate_24h}% ok
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
